import type { SVGProps } from 'react';

// Non-lucide glyphs ported 1:1 from the legacy map's public/*.svg assets
// (originally react-icons: bootstrap / game-icons / remix / tabler /
// material). Path data AND the baked stroke/fill treatment are copied
// verbatim from the legacy files so the map markers render identically.
// Colour flows through `currentColor`; `size` mirrors the lucide prop so
// these drop into the same render sites as lucide icons. `strokeWidth` is
// accepted-and-ignored: each glyph's weight is baked per the legacy asset.

type LegacyIconProps = Omit<
  SVGProps<SVGSVGElement>,
  'viewBox' | 'width' | 'height'
> & {
  size?: number | string;
  strokeWidth?: number | string;
};

// Activity.svg — bootstrap "person-walking", legacy-rendered as a thin
// outline (fill transparent, stroke 0.5).
export const PersonWalkingIcon = ({
  size = 24,
  strokeWidth: _strokeWidth,
  ...props
}: LegacyIconProps) => (
  <svg
    aria-hidden="true"
    fill="transparent"
    height={size}
    stroke="currentColor"
    strokeWidth="0.5"
    viewBox="-1 -1 18 18"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path d="M9.5 1.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0M6.44 3.752A.75.75 0 0 1 7 3.5h1.445c.742 0 1.32.643 1.243 1.38l-.43 4.083a1.8 1.8 0 0 1-.088.395l-.318.906.213.242a.8.8 0 0 1 .114.175l2 4.25a.75.75 0 1 1-1.357.638l-1.956-4.154-1.68-1.921A.75.75 0 0 1 6 8.96l.138-2.613-.435.489-.464 2.786a.75.75 0 1 1-1.48-.246l.5-3a.75.75 0 0 1 .18-.375l2-2.25Z" />
    <path d="M6.25 11.745v-1.418l1.204 1.375.261.524a.8.8 0 0 1-.12.231l-2.5 3.25a.75.75 0 1 1-1.19-.914zm4.22-4.215-.494-.494.205-1.843.006-.067 1.124 1.124h1.44a.75.75 0 0 1 0 1.5H11a.75.75 0 0 1-.531-.22Z" />
  </svg>
);

// Hiking.svg — game-icons "hiking", legacy-rendered as an outline
// (fill transparent, stroke 15 on the 512 grid).
export const HikingIcon = ({
  size = 24,
  strokeWidth: _strokeWidth,
  ...props
}: LegacyIconProps) => (
  <svg
    aria-hidden="true"
    fill="transparent"
    height={size}
    stroke="currentColor"
    strokeWidth="15"
    viewBox="0 0 512 512"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path d="M185 58.59c-15-.38-30 13.1-33.1 33.43-3.3 21.68 8.8 39.98 24.8 42.38 16 2.4 32.9-11.5 36.1-33.2 3.3-21.72-8.7-39.94-24.8-42.35-1-.15-2-.24-3-.26zm113.3 77.21l-17.2 5.2 8.3 28.1 18.9.5zm-144.6 7.6c-2.7 70.5-.4 140.9-3.6 202.5-15.8 35.8-36.7 70.2-50.38 99.2l25.68 12.4c20.8-33.7 42.2-68 55.2-102.9l11.3-63.4 53 45.9 16.2 88.1 29.3-5.8-17.6-100.6-58.1-53.6-9.3-86.4 48.1 31.3c26.7.9 45.1-1.9 69.6-5.7l1-16.3-70.8-2.1-46.5-42c-9.5 6.7-20.9 10-32.8 8.2-7.7-1.2-14.5-4.3-20.3-8.8zm-16.6 13l-40.47 11.7-5.96 85.5c13.53 6.7 31.03 11.6 43.23 3.4zm352.7 65.3l-95.7 78.1-32 51.9L324 222.5c-6.3.9-12.2 1.8-18 2.6l43.5 147.1-22.6 36.7-19.4 3.4 3.7 21.3-64.5 12.8-4.2-22.7-84.3 14.8c-9.5 15.6-17.1 27.9-26 42.2l-49.52-23.9-62.64 34.8H489.8z" />
  </svg>
);

// Safari.svg — game-icons "jeep", legacy-rendered as an outline
// (fill transparent, stroke 10 on the 512 grid).
export const JeepIcon = ({
  size = 24,
  strokeWidth: _strokeWidth,
  ...props
}: LegacyIconProps) => (
  <svg
    aria-hidden="true"
    fill="transparent"
    height={size}
    stroke="currentColor"
    strokeWidth="10"
    viewBox="0 0 512 512"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path d="M155.605 114.634l-14.31 7.154 3.576 7.156 13.423 26.844-57.578 57.578h22.625l42.494-42.494 21.248 42.494h17.89l-45.788-91.578-3.58-7.154zm132.422 2.732v24c0 11.313 5.527 24.452 12.13 39.47 6.345 14.432 13.97 29.757 19.87 42.33v54.2h-93.765l-16-48H80.027v80h2.623c5.445-11.528 14.993-20.257 26.176-26.092 12.89-6.725 28.058-9.908 43.2-9.908 15.144 0 30.31 3.183 43.203 9.908 11.18 5.835 20.73 14.564 26.174 26.092h117.344c10.22-23.39 31.87-35.904 54.555-37.967 23.296-2.118 47.98 5.742 65.738 21.966h7.222l10.666-32h-28.9v-48H333.09c-5.778-12.2-12.63-26.108-18.285-38.97-6.367-14.482-10.778-28.342-10.778-33.03v-24h-16zm-256.054 72v80h32v-80h-32zm432.054 23.88v32h16v-32h-16zm-231.568 32.12l5.333 16h50.234v-16H232.46zm-184.433 40v24.303h16v-24.304h-16zm104 16a48 48 0 0 0-48 48 48 48 0 0 0 48 48 48 48 0 0 0 48-48 48 48 0 0 0-48-48zm253.557 0a48 48 0 0 0-48 48 48 48 0 0 0 48 48 48 48 0 0 0 48-48 48 48 0 0 0-48-48zm-173.557 24v16h96v-16h-96zm-80 8a16 16 0 0 1 16 16 16 16 0 0 1-16 16 16 16 0 0 1-16-16 16 16 0 0 1 16-16zm253.557 0a16 16 0 0 1 16 16 16 16 0 0 1-16 16 16 16 0 0 1-16-16 16 16 0 0 1 16-16z" />
  </svg>
);

// Zoo.svg — game-icons "lion", legacy-rendered as an outline
// (fill transparent, stroke 10 on the 512 grid).
export const LionIcon = ({
  size = 24,
  strokeWidth: _strokeWidth,
  ...props
}: LegacyIconProps) => (
  <svg
    aria-hidden="true"
    fill="transparent"
    height={size}
    stroke="currentColor"
    strokeWidth="10"
    viewBox="0 0 512 512"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path d="M123.885 20.447c-10.348.467-21.337 3.146-32.194 5.366l62.492 65.628-21.645.188c-29.376.187-75.695 6.154-103.555 22.303l31.756 51.576-40.625 21.098v56.577l46.54 26.32-46.54 40.076v32.268c28.217-1.233 60.63.636 89.135-8.907l31.902-10.658-21.828 25.586c-18.76 22-29.782 55.133-50.736 87.713 31.91-2.394 63.352-7.65 96.8-25.81l16.026-8.683-9.746 77.124c26.333-12.19 52.103-28.923 71.178-49.055 22.683-23.94 35.713-50.49 33.36-72.71l-.074-.75c-18.488-12.687-32.26-31.173-39.236-54.18-8.414-27.753-5.867-62.33 6.643-101.145-26.406-4.04-46.536-14.637-57.307-30.11-12.33-17.716-14.222-39.26-7.738-57.018 6.483-17.758 22.29-32.165 43.437-33.4 20.558-1.195 44.48 10.033 69.972 36.102 11.368 4.11 21.73 8.034 31.14 11.94 2.434-21.395-1.46-38.033-10.408-52.9-8.41-13.975-23.217-26.43-41.1-38.582l4.27 42.888-16.425-10.985C222.37 43.57 168.973 18.49 123.885 20.597l.002-.225zm100.387 77.997c-.436.008-.866 0-1.29 0h-.002c-13.54.785-22.545 9.105-26.937 21.135-4.393 12.03-3.243 27.353 5.51 39.93 8.755 12.576 25.128 23.158 53.548 24.784l12.595.69-4.344 11.827c-14.837 40.46-16.242 73.928-8.577 99.212 7.66 25.267 24.04 42.795 47.305 52.56h.037l108.59 38.474c5.65-11.9 11.672-23.813 17.988-35.71-4.997 2.188-10.513 3.41-16.32 3.41-22.52 0-40.78-18.262-40.78-40.782 0-22.518 18.26-40.78 40.78-40.78 20.66 0 37.697 15.373 40.373 35.3 10.666-18.062 21.885-36 33.48-53.683l-103.224-52.377-4.746-5.732c-3.93-13.556-11.515-23.32-26.39-33.324-14.876-10.002-37.034-19.57-67.784-30.623l-2.08-.767-1.53-1.607c-23.283-24.532-42.7-32.278-56.202-32.024zm119.04 89.93c8.272 0 14.657 6.387 14.657 14.657 0 8.274-6.387 14.688-14.658 14.688-8.27 0-14.687-6.414-14.687-14.687 0-8.27 6.416-14.655 14.687-14.655z" />
  </svg>
);

// Museum.svg — material "museum", legacy-rendered filled.
export const MuseumIcon = ({
  size = 24,
  strokeWidth: _strokeWidth,
  ...props
}: LegacyIconProps) => (
  <svg
    aria-hidden="true"
    fill="currentColor"
    height={size}
    strokeWidth="1"
    viewBox="0 0 24 24"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path d="M0 0h24v24H0z" fill="none" />
    <path d="M22 11V9L12 2 2 9v2h2v9H2v2h20v-2h-2v-9h2zm-6 7h-2v-4l-2 3-2-3v4H8v-7h2l2 3 2-3h2v7z" />
  </svg>
);

// Spa.svg — tabler "massage", legacy-rendered as a thin outline
// (fill transparent, stroke 1, round caps).
export const MassageIcon = ({
  size = 24,
  strokeWidth: _strokeWidth,
  ...props
}: LegacyIconProps) => (
  <svg
    aria-hidden="true"
    fill="transparent"
    height={size}
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="1"
    viewBox="0 0 24 24"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path d="M4 17m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
    <path d="M9 5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
    <path d="M4 22l4 -2v-3h12" />
    <path d="M11 20h9" />
    <path d="M8 14l3 -2l1 -4c3 1 3 4 3 6" />
  </svg>
);

// Teamactivities.svg — remix "team-line", legacy-rendered filled with a
// 1px stroke on top.
export const TeamIcon = ({
  size = 24,
  strokeWidth: _strokeWidth,
  ...props
}: LegacyIconProps) => (
  <svg
    aria-hidden="true"
    fill="currentColor"
    height={size}
    stroke="currentColor"
    strokeWidth="1"
    viewBox="0 0 24 24"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path d="M12 11C14.7614 11 17 13.2386 17 16V22H15V16C15 14.4023 13.7511 13.0963 12.1763 13.0051L12 13C10.4023 13 9.09634 14.2489 9.00509 15.8237L9 16V22H7V16C7 13.2386 9.23858 11 12 11ZM5.5 14C5.77885 14 6.05009 14.0326 6.3101 14.0942C6.14202 14.594 6.03873 15.122 6.00896 15.6693L6 16L6.0007 16.0856C5.88757 16.0456 5.76821 16.0187 5.64446 16.0069L5.5 16C4.7203 16 4.07955 16.5949 4.00687 17.3555L4 17.5V22H2V17.5C2 15.567 3.567 14 5.5 14ZM18.5 14C20.433 14 22 15.567 22 17.5V22H20V17.5C20 16.7203 19.4051 16.0796 18.6445 16.0069L18.5 16C18.3248 16 18.1566 16.03 18.0003 16.0852L18 16C18 15.3343 17.8916 14.694 17.6915 14.0956C17.9499 14.0326 18.2211 14 18.5 14ZM5.5 8C6.88071 8 8 9.11929 8 10.5C8 11.8807 6.88071 13 5.5 13C4.11929 13 3 11.8807 3 10.5C3 9.11929 4.11929 8 5.5 8ZM18.5 8C19.8807 8 21 9.11929 21 10.5C21 11.8807 19.8807 13 18.5 13C17.1193 13 16 11.8807 16 10.5C16 9.11929 17.1193 8 18.5 8ZM5.5 10C5.22386 10 5 10.2239 5 10.5C5 10.7761 5.22386 11 5.5 11C5.77614 11 6 10.7761 6 10.5C6 10.2239 5.77614 10 5.5 10ZM18.5 10C18.2239 10 18 10.2239 18 10.5C18 10.7761 18.2239 11 18.5 11C18.7761 11 19 10.7761 19 10.5C19 10.2239 18.7761 10 18.5 10ZM12 2C14.2091 2 16 3.79086 16 6C16 8.20914 14.2091 10 12 10C9.79086 10 8 8.20914 8 6C8 3.79086 9.79086 2 12 2ZM12 4C10.8954 4 10 4.89543 10 6C10 7.10457 10.8954 8 12 8C13.1046 8 14 7.10457 14 6C14 4.89543 13.1046 4 12 4Z" />
  </svg>
);
